import React, { useState } from 'react';
import { useSetClassOverrideMutation } from '../../store/api/localApi';

export interface OverrideModalProps {
  classId: string;
  onClose: () => void;
}

const OverrideModal: React.FC<OverrideModalProps> = ({ classId, onClose }) => {
  const [setOverride, { isLoading }] = useSetClassOverrideMutation();
  const [form, setForm] = useState({
    date: '', status: 'CANCELLED' as 'CANCELLED' | 'MODIFIED',
    reason: '', startTime: '', endTime: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.date) { setError('Date is required'); return; }
    try {
      await setOverride({
        classId,
        date: form.date,
        status: form.status,
        reason: form.reason || undefined,
        startTime: form.status === 'MODIFIED' ? form.startTime : undefined,
        endTime: form.status === 'MODIFIED' ? form.endTime : undefined,
      }).unwrap();
      onClose();
    } catch (err: any) {
      setError(err.data?.error || 'Failed to set override');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="override-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cancel or Modify a Day</h3>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} min={new Date().toISOString().split('T')[0]} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Action *</label>
              <div className="mt-2 flex gap-3">
                <button type="button" onClick={() => setForm(p => ({ ...p, status: 'CANCELLED' }))}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border ${form.status === 'CANCELLED' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >Cancel Class</button>
                <button type="button" onClick={() => setForm(p => ({ ...p, status: 'MODIFIED' }))}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border ${form.status === 'MODIFIED' ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >Modify Time</button>
              </div>
            </div>
            {form.status === 'MODIFIED' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Start Time *</label>
                  <input type="time" value={form.startTime} onChange={e => setForm(p => ({ ...p, startTime: e.target.value }))} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">New End Time *</label>
                  <input type="time" value={form.endTime} onChange={e => setForm(p => ({ ...p, endTime: e.target.value }))} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Reason</label>
              <input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Teacher training day" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={isLoading} className={`px-4 py-2 border border-transparent rounded-md text-sm text-white disabled:opacity-50 ${form.status === 'CANCELLED' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}>
                {isLoading ? 'Saving...' : form.status === 'CANCELLED' ? 'Cancel Class' : 'Modify Class'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OverrideModal;
